import { useState, useContext, useEffect } from "react";
import { LoginContext } from "../LoginContext";
import { useTranslation } from "react-i18next";
import { useNotification } from "../contexts/NotificationContext";
import apiService from "../backend/api";
import { X, User, Phone, Mail, Building2, MapPin, Save, Loader2 } from "lucide-react";
import useProfile from "../hooks/useProfile";

const EditProfileModal = ({ onClose, onSave }) => {
  const { loginInfo } = useContext(LoginContext);
  const loggedInUser = loginInfo?.username;
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    street: "",
    houseNumber: "",
    zipCode: "",
    city: "",
    country: "",
  });

  const { data: profileData, updateProfile, isUpdating } = useProfile(loggedInUser);

  useEffect(() => {
    if (profileData && !profileData.error) {
      setProfile({
        firstName: profileData.firstName || "",
        lastName: profileData.lastName || "",
        email: profileData.email || "",
        phone: profileData.phone || "",
        company: profileData.company || "",
        billingAddress: profileData.billingAddress || "",
        street: profileData.street || "",
        houseNumber: profileData.houseNumber || "",
        zipCode: profileData.zipCode || "",
        city: profileData.city || "",
        country: profileData.country || "",
      });
    }
  }, [profileData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateProfile(profile, {
      onSuccess: (updatedData) => {
        if (onSave) onSave(updatedData);
        showNotification(t("editeprofile.Profil erfolgreich aktualisiert", "Profil erfolgreich aktualisiert"), "success");
        onClose();
      },
      onError: (error) => {
        console.error("Fehler beim Aktualisieren des Profils:", error);
        showNotification(t("editeprofile.Fehler beim Aktualisieren", "Fehler beim Aktualisieren der Daten. Bitte versuchen Sie es erneut."), "error");
      }
    });
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300">
      <div className="w-full max-w-lg bg-slate-900 border border-white/10 rounded-2xl sm:rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-500 max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-6 border-b border-white/5 flex items-center justify-between bg-slate-800/50">
          <div>
            <h2 className="text-xl font-black text-white">{t("editeprofile.Profil bearbeiten")}</h2>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-0.5">{loginInfo?.username}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/5 rounded-xl text-slate-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 md:p-8 overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* First Name */}
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-[#0f7699] ml-1">
                {t("editeprofile.Vorname")}
              </label>
              <div className="relative group">
                <input
                  name="firstName"
                  value={profile.firstName}
                  onChange={handleChange}
                  required
                  className="w-full bg-slate-800/50 border-2 border-slate-700/50 rounded-2xl px-5 py-3.5 text-white focus:outline-none focus:border-[#0f7699] transition-all font-bold"
                />
                <User size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-[#0f7699] transition-colors" />
              </div>
            </div>

            {/* Last Name */}
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-[#0f7699] ml-1">
                {t("editeprofile.Nachname")}
              </label>
              <div className="relative group">
                <input
                  name="lastName"
                  value={profile.lastName}
                  onChange={handleChange}
                  required
                  className="w-full bg-slate-800/50 border-2 border-slate-700/50 rounded-2xl px-5 py-3.5 text-white focus:outline-none focus:border-[#0f7699] transition-all font-bold"
                />
                <User size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-[#0f7699] transition-colors" />
              </div>
            </div>

            {/* Email - Full Width (Read Only) */}
            <div className="sm:col-span-2 space-y-2 opacity-80">
              <label className="text-[10px] font-black uppercase tracking-widest text-[#0f7699] ml-1">
                {t("editeprofile.E-Mail-Adresse")} <span className="text-slate-500 font-bold ml-1">({t("editeprofile.Nicht änderbar", "Nicht änderbar")})</span>
              </label>
              <div className="relative group cursor-not-allowed">
                <input
                  name="email"
                  type="email"
                  value={profile.email}
                  readOnly
                  className="w-full bg-slate-800/30 border-2 border-slate-700/30 rounded-2xl px-5 py-3.5 text-slate-400 focus:outline-none cursor-not-allowed font-bold"
                />
                <Mail size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-600 transition-colors" />
              </div>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-[#0f7699] ml-1">
                {t("editeprofile.Telefonnummer")}
              </label>
              <div className="relative group">
                <input
                  name="phone"
                  value={profile.phone}
                  onChange={handleChange}
                  required
                  className="w-full bg-slate-800/50 border-2 border-slate-700/50 rounded-2xl px-5 py-3.5 text-white focus:outline-none focus:border-[#0f7699] transition-all font-bold"
                />
                <Phone size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-[#0f7699] transition-colors" />
              </div>
            </div>

            {/* Company */}
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-[#0f7699] ml-1">
                {t("editeprofile.Firma")}
              </label>
              <div className="relative group">
                <input
                  name="company"
                  value={profile.company}
                  onChange={handleChange}
                  className="w-full bg-slate-800/50 border-2 border-slate-700/50 rounded-2xl px-5 py-3.5 text-white focus:outline-none focus:border-[#0f7699] transition-all font-bold"
                />
                <Building2 size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-[#0f7699] transition-colors" />
              </div>
            </div>

            {/* Billing Address - Full Width */}
            <div className="sm:col-span-2 space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-[#0f7699] ml-1">
                {t("editeprofile.Rechnungsadresse")}
              </label>
              <div className="relative group">
                <textarea
                  name="billingAddress"
                  value={profile.billingAddress}
                  onChange={handleChange}
                  rows={2}
                  className="w-full bg-slate-800/50 border-2 border-slate-700/50 rounded-2xl px-5 py-3.5 text-white focus:outline-none focus:border-[#0f7699] transition-all font-bold resize-none"
                />
                <MapPin size={18} className="absolute right-4 top-5 text-slate-500 group-focus-within:text-[#0f7699] transition-colors" />
              </div>
            </div>
          </div>

          <div className="mt-4 sm:mt-8 flex gap-3 sm:gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 sm:py-4 px-4 sm:px-6 border-2 border-slate-700 text-slate-400 rounded-2xl font-black uppercase tracking-widest text-xs sm:text-sm hover:bg-slate-800 hover:text-white transition-all active:scale-95"
            >
              {t("Abbrechen")}
            </button>
            <button
              type="submit"
              disabled={isUpdating}
              className="flex-[1.5] py-3 sm:py-4 px-4 sm:px-6 bg-gradient-to-br from-[#0f7699] to-[#4c9fb9] text-white rounded-2xl font-black uppercase tracking-widest text-xs sm:text-sm shadow-xl hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isUpdating ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              {isUpdating ? t('editeprofile.Speichern...') : t('editeprofile.Speichern')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;
